/** Mirrors tokens.css. Every value here has a --color-* / --text-* twin.
 *  Components read from this object; nothing hard-codes a brand hex. */

export const color = {
  goldTint: 'var(--color-gold-tint, #FBF7F0)', gold200: '#EADCBD', gold300: '#DDC597',
  gold: '#C8A46A', goldInk: 'var(--color-gold-ink, #8A6A2F)',
  black: '#151515', charcoal: '#242424', basalt700: '#1E1E1E',
  /** A strong fill and its text: black with white in light mode, lifted to warm charcoal in dark (theme-dark.css), where plain black vanishes on the cards. */
  fillStrong: 'var(--color-fill-strong, #151515)', onFillStrong: 'var(--color-on-fill-strong, #FFFFFF)', fillRing: 'var(--color-fill-ring, transparent)',
  dim: '#8A8680', onDark: '#DEDAD3',
  page: 'var(--color-page, #FAF9F6)', card: 'var(--color-card, #FFFFFF)', muted: 'var(--color-muted, #F1F0ED)', sunken: 'var(--color-sunken, #EDEAE4)',
  border: 'var(--color-line, #DEDAD3)', borderMuted: 'var(--color-line-soft, #EDEAE4)', borderStrong: 'var(--color-line-strong, #C7C1B6)',
  ink: 'var(--color-ink, #252525)', inkSecondary: 'var(--color-ink-2, #4A4844)', textMuted: 'var(--color-muted-ink, #706D67)', faint: 'var(--color-faint, #A8A29A)',
} as const;

/** bg / fg / line. fg is AA on bg; line is for dots, borders, fills.
 *  The brand guide's base hexes fail AA as badge text — they live on `line`. */
export const tone = {
  success:  { bg: '#E9F5EF', fg: '#11694A', line: '#16845B' },
  warning:  { bg: '#FFF4D8', fg: '#8A5A12', line: '#B7791F' },
  critical: { bg: '#FCECEB', fg: '#A8352F', line: '#C2413B' },
  info:     { bg: '#EAF2FA', fg: '#2E6DA4', line: '#2E6DA4' },
  neutral:  { bg: '#F1F0ED', fg: '#706D67', line: '#C7C1B6' },
} as const;

/** label, fill, borderStyle, tint, ink, glyph, markInk.
 *  markInk is per channel: white on gold is 2.34:1 and on coral 3.05:1 — both fail. */
export const channel = {
  airbnb:            { label: 'Airbnb',      fill: '#FF5A5F', border: 'solid',  tint: '#FFF0F0', ink: '#B3383C', glyph: 'A',  markInk: '#151515' },
  booking_com:       { label: 'Booking.com', fill: '#003B95', border: 'solid',  tint: '#EAF0FA', ink: '#003B95', glyph: 'B',  markInk: '#FFFFFF' },
  agoda:             { label: 'Agoda',       fill: '#5C2D91', border: 'solid',  tint: '#F2ECF9', ink: '#4A2475', glyph: 'a',  markInk: '#FFFFFF' },
  direct:            { label: 'Direct',      fill: '#C8A46A', border: 'solid',  tint: '#FBF7F0', ink: '#8A6A2F', glyph: 'TC', markInk: '#151515' },
  maintenance_block: { label: 'Block',       fill: '#C2413B', border: 'dashed', tint: '#FCECEB', ink: '#A8352F', glyph: 'M',  markInk: '#FFFFFF' },
  owner_stay:        { label: 'Owner stay',  fill: '#706D67', border: 'dotted', tint: '#FAF9F6', ink: '#5C5952', glyph: 'O',  markInk: '#FFFFFF' },
} as const;

export const font = {
  sans: '"IBM Plex Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, monospace',
} as const;

/** No authored size below 11px. */
/** The six-step UI scale (caption 11, meta 12, copy 13, heading 15, title 19, headline 26), the same values as
 *  the text-caption ... text-headline utilities in src/styles/tokens.css. The older names map onto it. */
export const text = {
  caption: 11, meta: 12, copy: 13, heading: 15, title: 19, headline: 26,
  display: 40, pageTitle: 26, section: 19, cardTitle: 15,
  body: 13, small: 12, micro: 11,
} as const;

export const space = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32, 10: 40, 12: 48 } as const;
export const radius = { xs: 4, sm: 6, control: 8, card: 12, pill: 999 } as const;

export const shadow = {
  // Warm-tinted, layered: a hairline contact shadow plus a soft ambient one, so surfaces lift without a hard edge.
  card: '0 1px 2px 0 rgba(40,32,20,0.04), 0 2px 8px -2px rgba(40,32,20,0.06)',
  raised: '0 1px 2px 0 rgba(40,32,20,0.05), 0 6px 16px -4px rgba(40,32,20,0.10)',
  dropdown: '0 2px 4px -1px rgba(40,32,20,0.06), 0 12px 28px -6px rgba(40,32,20,0.16)',
  overlay: '0 4px 8px -2px rgba(21,21,21,0.08), 0 24px 48px -12px rgba(21,21,21,0.28)',
} as const;

export const motion = {
  fast: '120ms', standard: '200ms', slow: '240ms',
  ease: 'cubic-bezier(0.16, 1, 0.3, 1)',
} as const;

export const shell = {
  sidebar: 244, sidebarCollapsed: 68, contentMax: 1600,
  control: 40, controlSm: 36, controlTouch: 44,
  navItem: 34, navItemL3: 30,
} as const;

/** The faint 44px grid and the soft wash the designs lay over the colour glows (the engine's Beta pages draw the same). */
const dcGrid = 'linear-gradient(rgba(47,107,255,.07) 1px,transparent 1px) 0 0/44px 44px,linear-gradient(90deg,rgba(47,107,255,.07) 1px,transparent 1px) 0 0/44px 44px';

/** Dream Crafter's glass sidebar, from the Project Workspace designs: v10 "Vision" (light) and v13 "Night Glass" (dark).
 *  Only the Dream Crafter side of the sidebar uses these (Sidebar.tsx, PLAN-dc-sidebar.md). */
export const dcGlass = {
  light: {
    backdrop: 'linear-gradient(118deg,rgba(255,255,255,0) 58%,rgba(255,248,226,.85) 66%,rgba(255,255,255,0) 76%),'
      + 'radial-gradient(48% 52% at 14% 16%,#F6C9D6 0%,rgba(246,201,214,0) 72%),'
      + 'radial-gradient(52% 58% at 90% 34%,#BFE6EA 0%,rgba(191,230,234,0) 72%),'
      + 'radial-gradient(46% 46% at 40% 100%,#CBD3FA 0%,rgba(236,227,207,0) 72%),'
      + 'linear-gradient(160deg,#E6ECEA 0%,#ECEEF3 50%,#DDE2EC 100%)',
    ink: '#111216', text: '#4A4A4A', muted: '#6B6B6B', faint: '#5A5A5A', surface: '#FFFFFF', accent: '#3D5AFE',
    /** The page the Beta screens sit on: grid, wash, then the backdrop. */
    get page() { return `${dcGrid},linear-gradient(180deg,rgba(250,251,253,.30),rgba(236,239,245,.38)),${this.backdrop}`; },
  },
  dark: {
    backdrop: 'radial-gradient(45% 40% at 18% 12%,rgba(70,110,255,.35) 0%,rgba(70,110,255,0) 70%),'
      + 'radial-gradient(40% 40% at 88% 70%,rgba(150,90,255,.28) 0%,rgba(150,90,255,0) 70%),'
      + 'radial-gradient(35% 35% at 50% 100%,rgba(60,180,255,.18) 0%,rgba(60,180,255,0) 70%),'
      + 'linear-gradient(160deg,#151823 0%,#0D0F16 100%)',
    ink: '#EEF1F8', text: '#B9C0D0', muted: '#9AA2B4', faint: '#A9B0C1', surface: '#FFFFFF', accent: '#9FC0FF', base: '#151823',
    get page() { return `${dcGrid},linear-gradient(180deg,rgba(16,18,26,.25),rgba(16,18,26,.35)),${this.backdrop}`; },
  },
} as const;

/** lucide-react, the dependency already in houzs-erp and hookka-erp. */
export const iconSize = { sm: 16, nav: 17, base: 20, lg: 24 } as const;
export const iconStroke = 1.75;
